import React, { lazy, memo } from 'react';
import { Edit2, Trash2, Star } from 'lucide-react';
import { Button } from '../ui/Button';
import { ProjectData } from '../../hooks/useProjects';
interface ProjectTableProps {
  projects: ProjectData[];
  onEdit: (project: ProjectData) => void;
  onDelete: (id: string) => void;
  onToggleFeatured: (id: string) => Promise<boolean>;
}
// Memoizar la fila individual para evitar re-renders innecesarios
const ProjectRow = memo(({
  project,
  featuredCount,
  onEdit,
  onDelete,
  onToggleFeatured
}: {
  project: ProjectData;
  featuredCount: number;
  onEdit: (project: ProjectData) => void;
  onDelete: (id: string) => void;
  onToggleFeatured: (id: string) => Promise<boolean>;
}) => {
  const handleToggleFeatured = async () => {
    if (project.id) {
      const success = await onToggleFeatured(project.id);
      if (!success && !project.featured) {
        alert('Máximo 3 proyectos destacados. Quita uno antes de agregar otro.');
      }
    }
  };
  return <tr className="hover:bg-gray-50 transition-colors" data-id="element-280">
        <td className="whitespace-nowrap px-6 py-4" data-id="element-281">
          <button onClick={handleToggleFeatured} disabled={!project.featured && featuredCount >= 3} className={`p-1 rounded-full transition-colors ${project.featured ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-300 hover:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed'}`} title={project.featured ? 'Quitar de destacados' : featuredCount >= 3 ? 'Máximo 3 proyectos destacados' : 'Destacar proyecto'} data-id="element-282">
            <Star className="h-5 w-5" fill={project.featured ? 'currentColor' : 'none'} data-id="element-283" />
          </button>
        </td>
        <td className="whitespace-nowrap px-6 py-4" data-id="element-284">
          <div className="flex items-center" data-id="element-285">
            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 relative" data-id="element-286">
              {project.images && project.images.length > 0 ? <img className="h-full w-full object-cover" src={project.images[0]} alt="" loading="lazy" data-id="element-287" /> : <div className="h-full w-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs" data-id="element-288">
                  No img
                </div>}
              {project.images && project.images.length > 1 && <div className="absolute bottom-0 right-0 bg-black/50 text-white text-[8px] px-1 rounded-tl" data-id="element-289">
                  +{project.images.length - 1}
                </div>}
            </div>
            <div className="ml-4" data-id="element-290">
              <div className="text-sm font-medium text-gray-900" data-id="element-291">
                {project.name}
              </div>
              <div className="text-sm text-gray-500 truncate max-w-[200px]" data-id="element-292">
                {project.description}
              </div>
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4" data-id="element-293">
          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold leading-5 text-gray-800" data-id="element-294">
            {project.category}
          </span>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500" data-id="element-295">
          {project.year}
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500" data-id="element-296">
          {project.m2.toLocaleString()}
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500" data-id="element-297">
          {project.comuna}
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium" data-id="element-298">
          <div className="flex justify-end gap-2" data-id="element-299">
            <Button variant="ghost" size="sm" onClick={() => onEdit(project)} title="Editar" data-id="element-300">
              <Edit2 className="h-4 w-4" data-id="element-301" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => project.id && onDelete(project.id)} className="text-red-600 hover:text-red-900 hover:bg-red-50" title="Eliminar" data-id="element-302">
              <Trash2 className="h-4 w-4" data-id="element-303" />
            </Button>
          </div>
        </td>
      </tr>;
});
ProjectRow.displayName = 'ProjectRow';
export const ProjectTable = memo(function ProjectTable({
  projects,
  onEdit,
  onDelete,
  onToggleFeatured
}: ProjectTableProps) {
  const featuredCount = projects.filter(p => p.featured).length;
  return <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-304">
      <div className="overflow-x-auto" data-id="element-305">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-306">
          <thead className="bg-gray-50" data-id="element-307">
            <tr data-id="element-308">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-10" data-id="element-309">
                <Star className="h-4 w-4" data-id="element-310" />
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-311">
                Proyecto
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-312">
                Categoría
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-313">
                Año
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-314">
                M2
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-315">
                Comuna
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" data-id="element-316">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white" data-id="element-317">
            {projects.map(project => <ProjectRow key={project.id} project={project} featuredCount={featuredCount} onEdit={onEdit} onDelete={onDelete} onToggleFeatured={onToggleFeatured} data-id="element-318" />)}
          </tbody>
        </table>
      </div>
      {projects.length === 0 && <div className="p-12 text-center text-gray-500" data-id="element-319">
          No hay proyectos registrados. Comienza creando uno nuevo.
        </div>}
    </div>;
});